import * as React from "react"
import { ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// Multi-select combobox with per-item checkboxes. Used for FILTERS.
// Semantics: empty selection = no filtering (All). Selecting items ORs them
// within this field — every selected item must be present in the results.
export function MultiCombobox({ options, values = [], onValuesChange, placeholder = "All", searchPlaceholder = "Search...", className, ...props }) {
  const [open, setOpen] = React.useState(false)

  const valueToLabel = React.useMemo(() => {
    const map = {}
    options.forEach((option) => { map[option.value] = option.label })
    return map
  }, [options])

  const toggle = (v) => {
    const next = values.includes(v) ? values.filter((x) => x !== v) : [...values, v]
    onValuesChange(next)
  }

  const label = values.length === 0
    ? placeholder
    : values.length === 1
      ? (valueToLabel[values[0]] ?? values[0])
      : `${values.length} selected`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full h-12 justify-between bg-white border-slate-200 text-slate-900 font-normal", className)}
          {...props}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[180px] p-0 bg-white border-slate-200" align="start">
        <Command>
          {options.length > 6 && <CommandInput placeholder={searchPlaceholder} className="h-8" />}
          <CommandList>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => toggle(option.value)}
                  className="cursor-pointer"
                >
                  <Checkbox
                    checked={values.includes(option.value)}
                    className="mr-2 pointer-events-none"
                    tabIndex={-1}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {values.length > 0 && (
            <div className="border-t border-slate-200 p-1">
              <button
                type="button"
                onClick={() => onValuesChange([])}
                className="w-full text-xs text-center text-slate-500 hover:text-red-600 py-1"
                data-testid={props["data-testid"] ? `${props["data-testid"]}-clear` : undefined}
              >
                Clear selection
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
